/**
 * @fileoverview Own explicit creation/editing of queued Trello-task intent for a matter and assignee. Backend catalogs supply matter/attorney context; the backend owns accepted queue state and remote execution. The enclosing workspace receives refresh signals after mutation. A saved queue row does not itself prove remote card creation.
 */

import { useCallback, useContext, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import ToastContext from "../../ToastContext"
import api from "../../api"
import {
    setRefreshTrelloTasks
} from '../../store/CaseDetails/action'
import { AppButton, AppModal, AppToggle, AppCalendar, AppTextInput, AppSelect } from '../shared'



function addTimezoneOffset(date) {
    const timezoneOffsetInMinutes = new Date().getTimezoneOffset()
    const timezoneOffsetInHours = timezoneOffsetInMinutes / 60
    const newDate = new Date(date)
    newDate.setHours(newDate.getHours() + timezoneOffsetInHours)
    return newDate
}



function TrelloTaskForm({ substage, visible, setVisible, currentlyEditing, defaultType }) {
    const dispatch = useDispatch()
    const matter = useSelector(state => state.caseDetails.matter)
    const showToast = useContext(ToastContext)
    const [formContent, setFormContent] = useState({})
    const [matterOptions, setMatterOptions] = useState([])
    const [attorneysForSelectedMatter, setAttorneysForSelectedMatter] = useState([])
    const [loadingMatterOptions, setLoadingMatterOptions] = useState(false)
    const [loadingMatterAttorneys, setLoadingMatterAttorneys] = useState(false)

    const existingMatterNumber = matter?.MatterNumber || ''
    const existingAttorneyName = matter?.attorneys?.[0]?.Name || ''
    const needsMatterSelection = !currentlyEditing && (!existingMatterNumber || !existingAttorneyName)

    const setInitialFormContent = useCallback(

    () => {
        if (currentlyEditing) {
            const utcDate = new Date(currentlyEditing.Date)
            const localDate = addTimezoneOffset(utcDate)
            const utcDueDate = new Date(currentlyEditing.Deadline)
            const localDueDate = addTimezoneOffset(utcDueDate)
            const ce = {
                dueDate: localDueDate,
                date: localDate,
                name: currentlyEditing.Name,
                details: currentlyEditing.Details,
                type: currentlyEditing.TaskType,
                matterNumber: currentlyEditing.MatterNumber || '',
                attorneyName: currentlyEditing.AttorneyName || ''
            }
            setFormContent(ce)
        } else if (substage === 'Settled in Principal') {
            setFormContent(
                {
                    dueDate: new Date(),
                    date: new Date(new Date().setDate(new Date().getDate() - 10)),
                    name: 'Deadline to Execute Settlement Agreement',
                    details: '',
                    type: defaultType || null,
                    matterNumber: existingMatterNumber,
                    attorneyName: existingAttorneyName
                }
            )
        } else if (substage === 'Agreement Executed & Payment(s) Pending') {
            setFormContent(
                {
                    dueDate: new Date(),
                    date: new Date(new Date().setDate(new Date().getDate() - 5)),
                    name: 'Settlement Payment Due',
                    details: '',
                    type: defaultType || null,
                    matterNumber: existingMatterNumber,
                    attorneyName: existingAttorneyName
                }
            )
        } else {
            setFormContent(
                {
                    dueDate: new Date(),
                    date: new Date(),
                    name: '',
                    details: '',
                    type: defaultType || null,
                    matterNumber: existingMatterNumber,
                    attorneyName: existingAttorneyName
                }
            )
        }
    }, [currentlyEditing, defaultType, existingAttorneyName, existingMatterNumber, substage])

    useEffect(() => {
        if (visible) {
            setInitialFormContent()
        }
    }, [setInitialFormContent, visible])

    useEffect(() => {
        if (!visible || !needsMatterSelection) return

        setLoadingMatterOptions(true)
        api.get('/cases')
            .then((response) => {
                const options = (response.data || [])
                    .map((item) => ({ label: item.name, value: item.id }))
                    .sort((left, right) => left.label.localeCompare(right.label))
                setMatterOptions(options)
            })
            .catch((error) => {
                console.error('Error loading matter options:', error)
                showToast({ severity: 'error', summary: 'Error', detail: 'Unable to load matters' })
            })
            .finally(() => {
                setLoadingMatterOptions(false)
            })
    }, [needsMatterSelection, showToast, visible])

    useEffect(

    () => {
        if (!visible || !needsMatterSelection) return

        const selectedMatterNumber = formContent.matterNumber
        if (!selectedMatterNumber) {
            setAttorneysForSelectedMatter([])
            setFormContent((prev) => ({ ...prev, attorneyName: '' }))
            return
        }

        setLoadingMatterAttorneys(true)
        api.get(`get_attorneys_for_matter/${selectedMatterNumber}`)
            .then(

            (response) => {
                const attorneys = response.data?.attorneys || []
                const options = attorneys
                    .map((attorney) => ({ label: attorney.name, value: attorney.name, isPrimary: attorney.isPrimary }))
                    .sort((left, right) => left.label.localeCompare(right.label))
                setAttorneysForSelectedMatter(options)

                const primaryAttorney = attorneys.find((attorney) => attorney.isPrimary)?.name
                const firstAttorney = attorneys[0]?.name || ''

                setFormContent(

                (prev) => {
                    const currentAttorneyStillValid = options.some((option) => option.value === prev.attorneyName)
                    const nextAttorneyName = currentAttorneyStillValid ? prev.attorneyName : (primaryAttorney || firstAttorney)

                    if (prev.attorneyName === nextAttorneyName) {
                        return prev
                    }

                    return { ...prev, attorneyName: nextAttorneyName }
                })
            })
            .catch(

            (error) => {
                console.error('Error loading matter attorneys:', error)
                setAttorneysForSelectedMatter([])
                setFormContent((prev) => ({ ...prev, attorneyName: '' }))
                showToast({ severity: 'error', summary: 'Error', detail: 'Unable to load matter attorneys' })
            })
            .finally(() => {
                setLoadingMatterAttorneys(false)
            })
    }, [formContent.matterNumber, needsMatterSelection, showToast, visible])

    const resolvedAttorneyName = currentlyEditing?.AttorneyName || formContent.attorneyName || matter?.attorneys?.[0]?.Name || null
    const resolvedMatterNumber = currentlyEditing?.MatterNumber || formContent.matterNumber || matter?.MatterNumber || null
    const submitDisabled = !resolvedAttorneyName || !resolvedMatterNumber || !formContent.name?.trim() || currentlyEditing?.Popped || loadingMatterOptions || loadingMatterAttorneys

    const submit =

    () => {
        let apiMethod, endpoint
        const payload = {
            Name: formContent.name,
            Details: formContent.details,
            DueDate: formContent.dueDate,
            Date: formContent.date,
            AttorneyName: resolvedAttorneyName,
            TaskType: formContent.type || 'Not a DueDate', // Adjusted for edit
            MatterNumber: resolvedMatterNumber,
        }

        if (!payload.AttorneyName || !payload.MatterNumber) {
            showToast({ severity: 'error', summary: 'Error', detail: 'Task is missing matter context' })
            return
        }

        // Check if editing
        if (currentlyEditing) {
            apiMethod = api.put
            endpoint = `/update_trello_task/${currentlyEditing.id}` // Assuming 'id' is the identifier for the task
            payload.OldDetails = { // Example, adjust based on actual data needed for update
                Name: currentlyEditing.Name,
                Details: currentlyEditing.Details,
                DueDate: currentlyEditing.DueDate,
                Date: currentlyEditing.Date,
                AttorneyName: currentlyEditing.AttorneyName,
                TaskType: currentlyEditing.TaskType,
                MatterNumber: currentlyEditing.MatterNumber,
            }
        } else {
            apiMethod = api.post
            endpoint = '/queue_trello_task'
        }

        apiMethod(endpoint, payload)
            .then(response => {
                if (response.status === 201 || response.status === 200) { // Assuming 200 for successful update
                    showToast({
                        severity: 'success',
                        summary: 'Successful',
                        detail: currentlyEditing ? 'DueDate updated' : 'DueDate added'
                    })
                    dispatch(setRefreshTrelloTasks(true))
                    setVisible(false)
                } else {
                    console.error('Unexpected status code:', response.status)
                    showToast({ severity: 'error', summary: 'Error', detail: 'Unexpected status code' })
                }
            })
            .catch(error => {
                console.error('Error:', error)
                showToast({ severity: 'error', summary: 'Error', detail: error.message })
            })
    }

    const deleteTrelloTask =

    () => {
        api.delete(`/delete_trello_task/${currentlyEditing.id}`)
            .then(response => {
                if (response.status === 201) {
                    setVisible(false)
                    showToast({ severity: 'success', summary: 'Successful', detail: 'Task deleted' })
                    dispatch(setRefreshTrelloTasks(true))
                } else {
                    console.error('Unexpected status code:', response.status)
                    showToast({ severity: 'error', summary: 'Error', detail: 'Unexpected status code' })
                }
            })
            .catch(error => {
                console.error('Error:', error)
                showToast({ severity: 'error', summary: 'Error', detail: 'Unable to delete task' })
            })
    }

    useEffect(

    () => {
        // update date based on dueDate
        // For Settled In Principal should have a dueDate to execute agreement and a Trello card created 10 days before the dueDate
        //For the Payment Pending we should have the ability to enter one or more dueDates and Trello cards created 5 days before each dueDate.
        if (formContent.type === 'DueDate') {
            if (substage === 'Settled in Principal') {
                setFormContent(prev => ({ ...prev, date: new Date(new Date(formContent.dueDate).setDate(new Date(formContent.dueDate).getDate() - 10)) }))
            } else if (substage === 'Agreement Executed & Payment(s) Pending') {
                setFormContent(prev => ({ ...prev, date: new Date(new Date(formContent.dueDate).setDate(new Date(formContent.dueDate).getDate() - 5)) }))
            }
        }
    }, [formContent.type, formContent.dueDate, substage])


    const footerContent = (
        <div className="case-details-modal__actions">
            {currentlyEditing && <AppButton label="Delete" icon="pi pi-times" variant="danger" onClick={deleteTrelloTask} disabled={currentlyEditing.Popped} />}
            <AppButton
                label={currentlyEditing ? 'Update' : 'Submit'}
                icon="pi pi-check"
                variant="primary"
                onClick={submit}
                autoFocus
                disabled={submitDisabled}
            />
        </div>
    )

    return (
        <AppModal
            title={currentlyEditing ? (currentlyEditing.Popped ? 'Task Already Created' : 'Edit Queued Task') : 'Queue Trello Task'}
            description="Set the Trello timing, title, and matter context for this queued task without leaving case details."
            visible={visible}
            width="min(92vw, 760px)"
            onClose={() => setVisible(false)}
            footer={footerContent}
            className="case-details-modal"
        >
            <div className="case-details-modal__form">
                {needsMatterSelection && (
                    <section className="case-details-modal__section">
                        <div className="case-details-modal__section-header">
                            <div>
                                <h3 className="case-details-modal__section-title">Matter Context</h3>
                                <p className="case-details-modal__section-description">
                                    Choose the matter and assigned attorney that this queued Trello task should follow.
                                </p>
                            </div>
                        </div>
                        <div className="case-details-modal__section-body">
                            <div className="case-details-modal__grid">
                                <div className="case-details-modal__field">
                                    <label htmlFor="matterNumber" className="case-details-modal__label">Matter</label>
                                    <AppSelect
                                        id="matterNumber"
                                        value={formContent.matterNumber || ''}
                                        options={matterOptions}
                                        placeholder={loadingMatterOptions ? 'Loading matters...' : 'Select a matter'}
                                        onChange={(value) => setFormContent((prev) => ({ ...prev, matterNumber: value, attorneyName: '' }))}
                                        disabled={(currentlyEditing && currentlyEditing.Popped) || loadingMatterOptions}
                                    />
                                </div>

                                <div className="case-details-modal__field">
                                    <label htmlFor="attorneyName" className="case-details-modal__label">Attorney</label>
                                    <AppSelect
                                        id="attorneyName"
                                        value={formContent.attorneyName || ''}
                                        options={attorneysForSelectedMatter}
                                        placeholder={
                                            !formContent.matterNumber ? 'Select a matter first'
                                            : loadingMatterAttorneys ? 'Loading attorneys...'
                                            : attorneysForSelectedMatter.length === 0 ? 'No attorneys assigned to matter'
                                            : 'Select an attorney'
                                        }
                                        onChange={(value) => setFormContent((prev) => ({ ...prev, attorneyName: value }))}
                                        disabled={(currentlyEditing && currentlyEditing.Popped) || !formContent.matterNumber || loadingMatterAttorneys || attorneysForSelectedMatter.length === 0}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {!needsMatterSelection && (
                    <div className="case-details-modal__callout">
                        Task will be queued for <strong>{resolvedMatterNumber || 'this matter'}</strong>
                        {resolvedAttorneyName ? ` under ${resolvedAttorneyName}.` : '.'}
                    </div>
                )}

                <section className="case-details-modal__section">
                    <div className="case-details-modal__section-header">
                        <div>
                            <h3 className="case-details-modal__section-title">Schedule</h3>
                            <p className="case-details-modal__section-description">
                                Control whether this should be treated as a due date and when the Trello card should be created.
                            </p>
                        </div>
                    </div>
                    <div className="case-details-modal__section-body">
                        <div className="case-details-modal__toggle-card">
                            <div className="case-details-modal__toggle-copy">
                                <div className="case-details-modal__toggle-title">Mark as due date</div>
                                <div className="case-details-modal__toggle-description">
                                    Due date tasks include an explicit deadline alongside the Trello creation date.
                                </div>
                            </div>
                            <AppToggle
                                checked={formContent.type === 'DueDate'}
                                onChange={(e) => setFormContent((prev) => ({ ...prev, type: e.value ? 'DueDate' : 'Not a DueDate' }))}
                                disabled={currentlyEditing && currentlyEditing.Popped}
                            />
                        </div>

                        <div className="case-details-modal__grid">
                            {formContent.type === 'DueDate' && (
                                <div className="case-details-modal__field">
                                    <label htmlFor="dueDate" className="case-details-modal__label">Due Date</label>
                                    <AppCalendar
                                        value={formContent.dueDate}
                                        onChange={(e) => setFormContent((prev) => ({ ...prev, dueDate: e.value }))}
                                        disabled={currentlyEditing && currentlyEditing.Popped}
                                    />
                                </div>
                            )}

                            <div className="case-details-modal__field">
                                <label htmlFor="date" className="case-details-modal__label">Card Creation Date</label>
                                <AppCalendar
                                    value={formContent.date}
                                    onChange={(e) => setFormContent((prev) => ({ ...prev, date: e.value }))}
                                    disabled={currentlyEditing && currentlyEditing.Popped}
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <section className="case-details-modal__section">
                    <div className="case-details-modal__section-header">
                        <div>
                            <h3 className="case-details-modal__section-title">Task Content</h3>
                            <p className="case-details-modal__section-description">
                                Set the card title and any short internal details that should appear with the queued task.
                            </p>
                        </div>
                    </div>
                    <div className="case-details-modal__section-body">
                        <div className="case-details-modal__grid">
                            <div className="case-details-modal__field case-details-modal__field--full">
                                <label htmlFor="name" className="case-details-modal__label">Name</label>
                                <AppTextInput
                                    id="name"
                                    value={formContent.name}
                                    onChange={(e) => setFormContent((prev) => ({ ...prev, name: e.target?.value }))}
                                    disabled={currentlyEditing && currentlyEditing.Popped}
                                />
                            </div>

                            <div className="case-details-modal__field case-details-modal__field--full">
                                <label htmlFor="details" className="case-details-modal__label">Details</label>
                                <AppTextInput
                                    id="details"
                                    value={formContent.details}
                                    onChange={(e) => setFormContent((prev) => ({ ...prev, details: e.target?.value }))}
                                    disabled={currentlyEditing && currentlyEditing.Popped}
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </AppModal>
    )
}

export default TrelloTaskForm
